import type { FC } from 'react'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { Stars01Icon } from '@/components/ui/icons'
import { SpaceImagesContainer } from '@/features/photoset/components/space-images-atoms'
import { useImageSelectionApproval } from '@/features/photoset/queries/use-image-selection-approval'
import type { Image, Photoset, PhotosetSpace } from '@/features/photoset/types'
import { errorHandler } from '@/helpers/utils'

interface AiVettingSelectionSpaceImagesProps {
	photosetId: Photoset['id']
	photosetSpaceId: PhotosetSpace['id']
	images: Omit<Image, 'space'>[]
	spaceIndex: number
	onExpandImage: (spaceIndex: number, imageIndex: number) => void
}

export const AiVettingSelectionSpaceImages: FC<
	AiVettingSelectionSpaceImagesProps
> = ({ photosetId, photosetSpaceId, images, spaceIndex, onExpandImage }) => {
	const { mutate: aiVettingSelectionToggle } = useImageSelectionApproval({
		photosetId,
		photosetSpaceId,
		options: {
			onError: (error) => errorHandler(error)
		}
	})

	const onToggleClick = (image: Omit<Image, 'space'>) => {
		aiVettingSelectionToggle({
			imageId: image.id,
			need_manual_retouching: !image.need_manual_retouching
		})
	}

	return (
		<SpaceImagesContainer $gap={4}>
			{images.map((image, index) => (
				<ImageCard
					endSlot={
						<ExpandImageButton
							onClick={() => onExpandImage(spaceIndex, index)}
						/>
					}
					image={image}
					imageNameIcon={<Stars01Icon size={20} />}
					isSelected={image.need_manual_retouching}
					key={image.id}
					onSelect={() => onToggleClick(image)}
					thumbnail={image.ai_retouched_thumbnails?.xs}
				/>
			))}
		</SpaceImagesContainer>
	)
}
